import React, { useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import Box from '@mui/material/Box';
import { Route, Switch, useLocation } from 'react-router-dom';

import { Footer } from '../../blocks/Footer';
import { Header } from '../../blocks/Header';
import { Loader } from '../../components/Loader';
import { NewsPage } from '../../components/NewsItem';
import { NewsList } from '../../components/NewsList';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import RouteWithCode from '../../components/RouteWithCode/RouteWithCode';
import { DEFAULT_DISCOVER_URL } from '../../const';
import { GET_COURSES_EXIST_INFO, GET_COURSES_PROGRESS_INFO } from '../../graphql/courses';
import { GET_HAS_NEWS } from '../../graphql/local';
import { GET_NAVIGATION } from '../../graphql/navigation';
import { GET_LIST_PARTNERS_BY_FILTER } from '../../graphql/partners';
import { AnalyticsPage } from '../../pages/AnalyticsPage';
import { AssignedLearningPage } from '../../pages/AssignedLearningPage';
import { CoursesListPage } from '../../pages/CourcesListPage';
import { CourseDetailsPage } from '../../pages/CourseDetailsPage';
import { CourseProgress } from '../../pages/CourseProgress';
import { CourseSharePage } from '../../pages/CourseSharePage';
import { DashboardPage } from '../../pages/DashboardPage';
import { DiscoverPage } from '../../pages/DiscoverPage';
import { Homepage } from '../../pages/Homepage';
import { LoginPage } from '../../pages/LoginPage';
import { LogoutPage } from '../../pages/LogoutPage';
import { MyAccountPage } from '../../pages/MyAccountPage';
import NotFoundPage from '../../pages/NotFoundPage';
import { PathwayPage } from '../../pages/PathwayPage';
import { PricePage } from '../../pages/PricePage';
import { RegistrationPage } from '../../pages/RegistrationPage';
import { ResetPasswordPage } from '../../pages/ResetPasswordPage';
import { SharePage } from '../../pages/SharePage';
import { SingleVideoPage } from '../../pages/SingleVideoPage';
import { SsoLoginPage } from '../../pages/SsoLoginPage';
import { TestPage } from '../../pages/TestPage';
import { ThankYouPage } from '../../pages/ThankYouPage';
import usePageStyles from '../../style';
import { CourseStatus, LearnerInfo, NavigationType, Query, Tenant } from '../../types';
import getUserComUserId from '../../utils/helpers/getUserComUserId';
import { useAuth } from '../../utils/hooks';
import useVerse from '../../utils/hooks/useVerse';
import { CustomPageLayout } from '../CustomPageLayout';
import useStyles from './styles';

interface RXDLayoutProps {
  tenant: Tenant;
  userInfo: LearnerInfo;
  userLoading: boolean;
  authAppId?: string;
  waitForUser?: boolean;
  loadingTenant: boolean;
}

export type CourseDropdownItem = {
  id?: string;
  name?: string | null;
  navigationDescription?: string | null;
  slug?: string;
  isStarted: boolean;
};

export const Layout: React.FC<RXDLayoutProps> = ({
  tenant,
  loadingTenant,
  userInfo,
  userLoading,
  authAppId,
  waitForUser,
}) => {
  const classes = useStyles();
  const pageClasses = usePageStyles();
  const location = useLocation();
  const pathname = location?.pathname;
  const [isHeaderHidden, setIsHeaderHidden] = useState<boolean>(false);
  const { changeUserId } = useVerse();

  const { isAuthenticated } = useAuth();
  const logoUrl = userInfo?.company?.logo_url ? userInfo?.company?.logo_url : tenant?.logo_url;
  const partnerTypeId = tenant?.partner_type_id;
  const tenantTitle = tenant?.title || '';
  const tenantName = tenant?.name;
  const tenantId = tenant?.id;
  const tenantCircleUrl = tenant?.circle_image_url;
  const linkedInId = tenant?.linkedin_id;
  const assignedLearningsName = userInfo?.company?.short_name || '';

  const { data } = useQuery(GET_HAS_NEWS);
  const hasNews = data?.hasNews;

  const {
    data: listPartners,
    // loading: partnersCompanyLoading,
    // error: partnersCompanyError
  } = useQuery(GET_LIST_PARTNERS_BY_FILTER, {
    variables: {
      filter: {
        type_id: partnerTypeId,
      },
    },
  });

  const footerPartners = listPartners?.listPartners;

  const footerNavigationType = isAuthenticated
    ? NavigationType.FooterLoggedIn
    : NavigationType.FooterLoggedOut;
  const { data: navigationData } = useQuery(GET_NAVIGATION, {
    variables: {
      filter: {
        navigation_type: isAuthenticated
          ? [footerNavigationType]
          : [NavigationType.LogedOut, footerNavigationType],
      },
    },
    skip: !tenant,
  });

  const navigation = navigationData?.getNavigation;
  const headerNavigation = navigation?.find(
    (nav) => nav.navigation_type === NavigationType.LogedOut,
  );
  const footerNavigation = navigation?.find((nav) => nav.navigation_type === footerNavigationType);

  const { data: coursesExistData } =
    useQuery<Pick<Query, 'listCoursesLogedOut'>>(GET_COURSES_EXIST_INFO);

  const { data: coursesProgressData, refetch: refetchCourseProgressData } = useQuery<
    Pick<Query, 'listCoursesLogedIn'>
  >(GET_COURSES_PROGRESS_INFO, {
    skip: !isAuthenticated,
  });

  const coursesProgress = coursesProgressData?.listCoursesLogedIn || [];

  const hasAnotherCourses = useMemo(
    () =>
      coursesExistData?.listCoursesLogedOut && coursesExistData?.listCoursesLogedOut?.length > 1,
    [coursesExistData],
  );

  const dropdownCourses: CourseDropdownItem[] = useMemo(() => {
    const coursesList = coursesExistData?.listCoursesLogedOut;
    const coursesProgress = coursesProgressData?.listCoursesLogedIn;

    if (coursesList) {
      return coursesList.map((item) => ({
        id: item?.id,
        name: item?.name,
        slug: item?.slug,
        navigationDescription: item?.navigation_description,
        isStarted: Boolean(
          coursesProgress?.find(
            (progressItem) =>
              item?.id === progressItem?.id &&
              progressItem?.status &&
              progressItem.status !== CourseStatus.NotStarted,
          ),
        ),
      }));
    }

    return [];
  }, [coursesExistData, coursesProgressData]);

  useEffect(() => {
    if (userInfo) {
      changeUserId(userInfo?.id, userInfo?.email || '', getUserComUserId(userInfo?.id));
    }
  }, [userInfo, changeUserId]);

  return (
    <>
      <Header
        navigation={headerNavigation}
        assignedLearningsName={assignedLearningsName}
        logoUrl={logoUrl}
        dropdownCourses={dropdownCourses}
        isHeaderHidden={isHeaderHidden}
      />
      <Box className={classes.root}>
        <Switch>
          <Route
            path="/login/:ssoKey"
            render={(props) => (
              <SsoLoginPage
                {...props}
                userInfo={userInfo}
                userLoading={userLoading}
                tenant={tenant}
                loadingTenant={loadingTenant}
              />
            )}
          />
          <Route
            exact
            path={['/login', '/sso']}
            render={(props) => (
              <LoginPage {...props} userInfo={userInfo} userLoading={userLoading} />
            )}
          />
          <Route
            path="/logout"
            render={(props) => <LogoutPage {...props} loadingUser={userLoading} />}
          />
          <Route path="/registration" render={(props) => <RegistrationPage {...props} />} />
          <Route path="/forgot-password" render={(props) => <ResetPasswordPage {...props} />} />
          {hasNews && (
            <Route exact path="/news/:id">
              <div className={pageClasses.container}>
                <NewsPage fallback={<Loader />} />
              </div>
            </Route>
          )}
          {hasNews && (
            <Route exact path="/news">
              <div className={pageClasses.container}>
                <NewsList fallback={<Loader />} />
              </div>
            </Route>
          )}
          <Route exact path="/pricing">
            <div className={pageClasses.container}>
              <PricePage fallback={<Loader />} />
            </div>
          </Route>
          <ProtectedRoute
            exact
            withSubscription={false}
            path="/payment-successful"
            userInfo={userInfo}
            userLoading={userLoading}
            render={(props) => (
              <div className={pageClasses.container}>
                <ThankYouPage
                  tenantLinkedInId={linkedInId}
                  userInfo={userInfo}
                  tenantName={tenantName}
                  fallback={<Loader />}
                  {...props}
                />
              </div>
            )}
          />
          <Route
            exact={false}
            path="/analytics"
            render={(props) => (
              <div className={pageClasses.container}>
                <AnalyticsPage
                  userInfo={userInfo}
                  userLoading={userLoading}
                  tenantTitle={tenantTitle}
                  tenantName={tenantName}
                  {...props}
                  fallback={<Loader />}
                />
              </div>
            )}
          />
          <ProtectedRoute
            exact={false}
            path="/assigned-learning"
            userInfo={userInfo}
            userLoading={userLoading}
            render={(props) => (
              <div className={pageClasses.container}>
                <AssignedLearningPage
                  tenantTitle={tenantTitle}
                  tenantName={tenantName}
                  title={assignedLearningsName}
                  userInfo={userInfo}
                  {...props}
                />
              </div>
            )}
          />
          <ProtectedRoute
            exact
            path={['/home', '/recently-watched-my-watchlist/']}
            userInfo={userInfo}
            userLoading={userLoading}
            render={(props) => (
              <div className={pageClasses.container}>
                <DashboardPage
                  userInfo={userInfo}
                  tenantTitle={tenantTitle}
                  {...props}
                  fallback={<Loader />}
                />
              </div>
            )}
          />
          <Route
            exact
            path="/videos/:slug"
            render={(props) => (
              <div className={pageClasses.container}>
                <SingleVideoPage
                  tenantTitle={tenantTitle}
                  tenantName={tenantName}
                  userInfo={userInfo}
                  waitForUser={waitForUser}
                  fallback={<Loader />}
                  bumperUrl={tenant?.video_bumper_url}
                  setIsHeaderHidden={setIsHeaderHidden}
                  {...props}
                />
              </div>
            )}
          />
          <ProtectedRoute
            exact
            userInfo={userInfo}
            userLoading={userLoading}
            path={[
              '/pathway/:slug/test/:testId',
              `${DEFAULT_DISCOVER_URL}/pathways/:slug/test/:testId`,
            ]}
            render={(props) => (
              <div className={pageClasses.container}>
                <TestPage
                  tenantTitle={tenantTitle}
                  tenantName={tenantName}
                  tenantCircleUrl={tenantCircleUrl}
                  tenantId={tenantId}
                  userId={userInfo?.id}
                  fallback={<Loader />}
                  {...props}
                />
              </div>
            )}
          />
          <Route
            exact
            path={['/pathway/:slug', `${DEFAULT_DISCOVER_URL}/pathways/:slug`]}
            render={(props) => (
              <div className={pageClasses.container}>
                <PathwayPage
                  tenantCircleUrl={tenantCircleUrl}
                  userInfo={userInfo}
                  tenantTitle={tenantTitle}
                  tenantName={tenantName}
                  waitForUser={waitForUser}
                  fallback={<Loader />}
                  {...props}
                />
              </div>
            )}
          />
          <Route
            exact
            path="/share/courses/:hash"
            render={(props) => (
              <CourseSharePage
                tenantTitle={tenantTitle}
                tenantName={tenantName}
                {...props}
                fallback={<Loader />}
              />
            )}
          />
          <Route
            exact
            path="/share/:hash"
            render={(props) => (
              <SharePage
                tenantTitle={tenantTitle}
                tenantName={tenantName}
                {...props}
                fallback={<Loader />}
              />
            )}
          />
          <Route
            exact={false}
            path={[DEFAULT_DISCOVER_URL, '/pathways', '/videos', '/experts', '/expert/:expertSlug']}
            render={(props) => (
              <div className={pageClasses.container}>
                <DiscoverPage
                  tenant={tenant}
                  loadingTenant={loadingTenant}
                  userInfo={userInfo}
                  userLoading={userLoading}
                  fallback={<Loader />}
                  {...props}
                />
              </div>
            )}
          />
          <Route
            exact
            path="/courses/:slug"
            render={(props) => (
              <div className={pageClasses.container}>
                <CourseDetailsPage
                  {...props}
                  userInfo={userInfo}
                  tenant={tenant}
                  coursesProgress={coursesProgress}
                  hasAnotherCourses={hasAnotherCourses}
                  refetchCourseProgressData={refetchCourseProgressData}
                />
              </div>
            )}
          />
          <ProtectedRoute
            exact
            path="/courses/:slug/progress"
            userInfo={userInfo}
            userLoading={userLoading}
            render={(props) => (
              <div className={pageClasses.container}>
                <CourseProgress
                  {...props}
                  userInfo={userInfo}
                  tenant={tenant}
                  hasAnotherCourses={hasAnotherCourses}
                  fallback={<Loader />}
                />
              </div>
            )}
          />
          <Route
            exact
            path="/courses"
            render={(props) => (
              <div className={pageClasses.container}>
                <CoursesListPage tenant={tenant} {...props} />
              </div>
            )}
          />
          <RouteWithCode exact path="/404" code={404}>
            <NotFoundPage />
          </RouteWithCode>
          <ProtectedRoute
            exact={false}
            path="/my-account"
            userInfo={userInfo}
            userLoading={userLoading}
            withSubscription={false}
            render={(props) => (
              <div className={pageClasses.container}>
                <MyAccountPage
                  {...props}
                  tenant={tenant}
                  userInfo={userInfo}
                  authAppId={authAppId}
                  fallback={<Loader />}
                />
              </div>
            )}
          />
          <Route
            exact
            path="/"
            render={(props) => (
              <div className={pageClasses.container}>
                <Homepage
                  {...props}
                  userInfo={userInfo}
                  userLoading={userLoading}
                  tenant={tenant}
                />
              </div>
            )}
          />
          <Route exact path={pathname}>
            <CustomPageLayout userInfo={userInfo} tenant={tenant} />
          </Route>
        </Switch>
      </Box>
      <Footer
        footerPartners={footerPartners}
        navigation={footerNavigation}
        tenantTitle={tenantTitle}
        linkedinLink={tenant?.linkedin_link}
        twitterLink={tenant?.twitter_link}
        facebookLink={tenant?.facebook_link}
        instagramLink={tenant?.instagram_link}
      />
      <div id="below-footer"></div>
    </>
  );
};

export default Layout;
